import { capitalize } from 'lodash';
import Papa from 'papaparse';
import { ProductIDOptions } from 'src/utils/stripeConfig';
import { callApi } from 'src/core/callApi';

const ADMIN_DAILY_SIGNUPS_LOG = '[admin daily signups]';

export const GET_AIRBTICS_USER_DETAILS_URL =
  'https://b9af2vkl1m.execute-api.us-east-2.amazonaws.com/default/getAirbticsUserDetails';

/** Normalize CSV header cells (UTF-8 BOM breaks strict `row['Email']` lookups). */
function normalizeHeaderKey(h: string): string {
  return h.replace(/^\uFEFF/g, '').trim();
}

function getByHeader(row: Record<string, string>, ...candidates: string[]): string {
  for (const c of candidates) {
    const v = row[c];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  const norm = (s: string) => normalizeHeaderKey(s).toLowerCase();
  for (const key of Object.keys(row)) {
    if (candidates.some(c => norm(key) === norm(c))) {
      const v = row[key];
      if (v != null && String(v).trim() !== '') return String(v);
    }
  }
  return '';
}

function parseDailyNewUsersCsvText(text: string) {
  const cleaned = text.replace(/^\uFEFF/, '');
  console.log(
    ADMIN_DAILY_SIGNUPS_LOG,
    'parseDailyNewUsersCsvText: input length',
    text?.length ?? 0,
    'first line preview:',
    cleaned.split(/\r?\n/)[0]?.slice(0, 200) ?? ''
  );
  const parsed = Papa.parse<Record<string, string>>(cleaned, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h: string) => normalizeHeaderKey(h),
  });
  if (parsed.errors?.length) {
    console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'CSV parse warnings', parsed.errors);
  }
  const fields = parsed.meta?.fields ?? [];
  const firstRow = (parsed.data && parsed.data[0]) as Record<string, string> | undefined;
  console.log(ADMIN_DAILY_SIGNUPS_LOG, 'Papa meta.fields:', fields);
  console.log(ADMIN_DAILY_SIGNUPS_LOG, 'first row keys:', firstRow ? Object.keys(firstRow) : [], 'sample:', firstRow);
  const raw = (parsed.data || []).filter(row => {
    if (!row || typeof row !== 'object') return false;
    return getByHeader(row, 'Email', 'email').trim().length > 0;
  });
  console.log(ADMIN_DAILY_SIGNUPS_LOG, 'rows with email after filter:', raw.length, '/', (parsed.data || []).length);
  return raw.map((row, index) => {
    const email = getByHeader(row, 'Email', 'email').trim();
    return {
      id: email || `row-${index}`,
      name: getByHeader(row, 'Name', 'name'),
      email,
      searchedMarkets: getByHeader(row, 'Searched Markets'),
      userDescription: getByHeader(row, 'User Description'),
      userGoal: getByHeader(row, 'User Goal'),
      userStrategy: getByHeader(row, 'User Strategy'),
      isBusinessUser: getByHeader(row, 'Is Business User'),
      signupAt: getByHeader(row, 'Signup Date & Time (UTC)'),
    };
  });
}

/** Unwrap API Gateway / Lambda-proxy shapes so `message.csv_text` is reachable. */
function extractDailyReportPayload(res: any): { csv_text?: string; s3_key?: string; error?: string } | null {
  if (!res || typeof res !== 'object') {
    console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'extractDailyReportPayload: not an object', res);
    return null;
  }

  let root: any = res;
  if (typeof res.body === 'string') {
    try {
      root = JSON.parse(res.body);
      console.log(ADMIN_DAILY_SIGNUPS_LOG, 'unwrapped string res.body → keys:', Object.keys(root));
    } catch (e) {
      console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'extractDailyReportPayload: JSON.parse(res.body) failed', e);
      return null;
    }
  } else if (res.body != null && typeof res.body === 'object') {
    root = res.body;
    console.log(ADMIN_DAILY_SIGNUPS_LOG, 'using object res.body → keys:', Object.keys(root));
  } else {
    console.log(ADMIN_DAILY_SIGNUPS_LOG, 'top-level response keys (no body unwrap):', Object.keys(res));
  }

  let msg = root?.message;
  if (msg === 'Not a Admin User') {
    return { error: 'Not a Admin User' };
  }
  if (typeof msg === 'string') {
    try {
      msg = JSON.parse(msg);
    } catch {
      return typeof msg === 'string' && msg.length > 0 ? { error: msg } : null;
    }
  }
  if (msg && typeof msg === 'object') {
    const m = msg as { csv_text?: string; s3_key?: string; error?: string };
    console.log(
      ADMIN_DAILY_SIGNUPS_LOG,
      'message object keys:',
      Object.keys(m),
      'csv_text length:',
      typeof m.csv_text === 'string' ? m.csv_text.length : '(none)',
      's3_key:',
      m.s3_key,
      'error:',
      m.error
    );
    return m;
  }
  console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'extractDailyReportPayload: no usable message', { rootKeys: Object.keys(root) });
  return null;
}

/**
 * Backend lists S3 bucket `airbtics-customer-daily-report` for `new_users_signup_*.csv` and returns the newest by LastModified.
 * getAirbticsUserDetails: `operation=list_latest_daily_new_users_csv` + admin_password. Lambda needs s3:ListBucket + s3:GetObject.
 */
export const fetchDailyNewUsersFromAdminApi = async (
  adminPassword: string
): Promise<
  | { ok: true; rows: any[]; count: number; s3Key: string }
  | { ok: false; error: string }
> => {
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        operation: 'list_latest_daily_new_users_csv',
      },
    },
    false
  );

  if (typeof res === 'string') {
    console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'callApi returned non-JSON error string:', res);
    return { ok: false, error: res };
  }

  console.log(ADMIN_DAILY_SIGNUPS_LOG, 'fetch: response summary', {
    type: typeof res,
    keys: res && typeof res === 'object' ? Object.keys(res) : [],
    hasBodyString: typeof res?.body === 'string',
    hasBodyObject: res?.body != null && typeof res?.body === 'object',
  });

  const payload = extractDailyReportPayload(res);
  if (payload?.error === 'Not a Admin User') {
    console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'admin password rejected by API');
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (payload?.error) {
    console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'API error payload:', payload.error);
    return { ok: false, error: String(payload.error) };
  }
  if (payload && typeof payload.csv_text === 'string') {
    const rows = parseDailyNewUsersCsvText(payload.csv_text);
    console.log(ADMIN_DAILY_SIGNUPS_LOG, 'success: row count', rows.length, 's3Key', payload.s3_key);
    return {
      ok: true,
      rows,
      count: rows.length,
      s3Key: payload.s3_key || '',
    };
  }
  console.warn(ADMIN_DAILY_SIGNUPS_LOG, 'unexpected: payload after extract', payload);
  return {
    ok: false,
    error:
      'Unexpected response from admin API (missing csv_text). Check browser Network tab for response shape.',
  };
};

/** Unwrap Lambda-proxy / API Gateway JSON to `message` object (any shape). */
function unwrapAdminMessage(res: any): any {
  if (!res || typeof res !== 'object') {
    return null;
  }
  let root: any = res;
  if (typeof res.body === 'string') {
    try {
      root = JSON.parse(res.body);
    } catch {
      return null;
    }
  } else if (res.body != null && typeof res.body === 'object') {
    root = res.body;
  }
  let msg = root?.message;
  if (msg === 'Not a Admin User') {
    return { error: 'Not a Admin User' };
  }
  if (typeof msg === 'string') {
    try {
      msg = JSON.parse(msg);
    } catch {
      return { error: msg };
    }
  }
  return msg;
}

export type PublicApiLedgerPayload = {
  grouped_usage_data: Record<string, Record<string, number>>;
  /** UTC calendar day (YYYY-MM-DD) -> sum of `cost` from ledger for that day */
  daily_cost_totals?: Record<string, number>;
  /** Sum of all `cost` values in the scan window */
  total_cost_in_window?: number;
  /** Per-user sum of `cost` over the window (same 30 days); only rows with total_spent > 0 */
  user_spending?: Array<{ user_email: string; total_spent: number }>;
  recent_calls: Array<{
    user_email: string;
    timestamp: number;
    route_key: string;
    cost: number;
  }>;
  /** Omitted on older API responses; treated as [] in fetchPublicApiLedgerAdmin. */
  failed_recent_calls?: Array<{
    user_email: string;
    timestamp: number;
    route_key: string;
    cost: number;
    deny_reason?: string;
  }>;
  failed_recent_limit?: number;
  total_in_window: number;
  days?: number;
  recent_limit?: number;
  error?: string;
};

/** getAirbticsUserDetails: operation=list_public_api_usage_ledger_admin */
export const fetchPublicApiLedgerAdmin = async (
  adminPassword: string,
  opts?: { failedEmailSearch?: string }
): Promise<{ ok: true; data: PublicApiLedgerPayload } | { ok: false; error: string }> => {
  const q: Record<string, string> = {
    admin_password: encodeURIComponent(adminPassword),
    operation: 'list_public_api_usage_ledger_admin',
  };
  const fe = opts?.failedEmailSearch?.trim();
  if (fe) {
    q.failed_email_search = encodeURIComponent(fe);
  }
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: q,
    },
    false
  );

  if (typeof res === 'string') {
    return { ok: false, error: res };
  }

  const msg = unwrapAdminMessage(res);
  if (!msg) {
    return { ok: false, error: 'Unexpected response from admin API.' };
  }
  if (msg.error === 'Not a Admin User') {
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (msg.error) {
    return { ok: false, error: String(msg.error) };
  }
  if (
    msg.grouped_usage_data &&
    typeof msg.grouped_usage_data === 'object' &&
    Array.isArray(msg.recent_calls)
  ) {
    const data = {
      ...msg,
      failed_recent_calls: Array.isArray(msg.failed_recent_calls)
        ? msg.failed_recent_calls
        : [],
    } as PublicApiLedgerPayload;
    return { ok: true, data };
  }
  return { ok: false, error: 'Unexpected response shape from admin API.' };
};

export type AffiliateMonthlySalesPayload = {
  month: string;
  total_partners_with_sales: number;
  total_sales_count: number;
  partners: Array<{
    partner_key: string;
    partner_email: string;
    partner_name: string;
    sales_count: number;
    unique_sales_users_count: number;
    active_days_count: number;
    totals_by_currency: Record<string, number>;
    sales_details: Array<{
      sale_date: string;
      sold_user_email: string;
      payment_price: number | null;
      currency: string;
    }>;
  }>;
  error?: string;
};

export type DataProcessRow = {
  id: number;
  run_date: string;
  process_name: string;
  status: string;
  fill_percent: number;
  total_count: number;
  updated_at: string | null;
  /** Calendar table row count denominator for UI fill % (from backend config). */
  full_total_count?: number | null;
  /** Pass/fail threshold for calendar fill % (from backend config). */
  success_fill_percent_threshold?: number | null;
};

export type DataProcessAdminPayload = {
  rows: DataProcessRow[];
  error?: string;
};

export type OtaDataProcessRow = {
  id: string;
  crawl_date: string;
  source: string;
  row_count: number;
  supply_avg: number | null;
};

export type OtaDataProcessAdminPayload = {
  rows: OtaDataProcessRow[];
  error?: string;
};

/** getAirbticsUserDetails: operation=list_affiliate_monthly_sales_admin&month=YYYY-MM */
export const fetchAffiliateMonthlySalesAdmin = async (
  adminPassword: string,
  month: string
): Promise<{ ok: true; data: AffiliateMonthlySalesPayload } | { ok: false; error: string }> => {
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: {
        admin_password: encodeURIComponent(adminPassword),
        operation: 'list_affiliate_monthly_sales_admin',
        month,
      },
    },
    false
  );

  if (typeof res === 'string') {
    return { ok: false, error: res };
  }

  const msg = unwrapAdminMessage(res);
  if (!msg) {
    return { ok: false, error: 'Unexpected response from admin API.' };
  }
  if (msg.error === 'Not a Admin User') {
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (msg.error) {
    return { ok: false, error: String(msg.error) };
  }
  if (msg.month && Array.isArray(msg.partners)) {
    return { ok: true, data: msg as AffiliateMonthlySalesPayload };
  }
  return { ok: false, error: 'Unexpected affiliate monthly response shape.' };
};

/** getAirbticsUserDetails: operation=list_data_process_admin */
export const fetchDataProcessAdmin = async (
  adminPassword: string,
  opts?: { month?: string; year?: string; period?: string }
): Promise<{ ok: true; data: DataProcessAdminPayload } | { ok: false; error: string }> => {
  const q: Record<string, string> = {
    admin_password: encodeURIComponent(adminPassword),
    operation: 'list_data_process_admin',
  };
  if (opts?.month?.trim()) {
    q.month = opts.month.trim();
  }
  if (opts?.year?.trim()) {
    q.year = opts.year.trim();
  }
  if (opts?.period?.trim()) {
    q.period = opts.period.trim();
  }
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: q,
    },
    false
  );

  if (typeof res === 'string') {
    return { ok: false, error: res };
  }

  const msg = unwrapAdminMessage(res);
  if (!msg) {
    return { ok: false, error: 'Unexpected response from admin API.' };
  }
  if (msg.error === 'Not a Admin User') {
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (msg.error) {
    return { ok: false, error: String(msg.error) };
  }
  if (Array.isArray(msg.rows)) {
    return {
      ok: true,
      data: {
        rows: msg.rows as DataProcessRow[],
      },
    };
  }
  return { ok: false, error: 'Unexpected data process response shape.' };
};

/** getAirbticsUserDetails: operation=list_ota_data_process_admin */
export const fetchOtaDataProcessAdmin = async (
  adminPassword: string,
  opts?: { month?: string; year?: string; period?: string }
): Promise<{ ok: true; data: OtaDataProcessAdminPayload } | { ok: false; error: string }> => {
  const q: Record<string, string> = {
    admin_password: encodeURIComponent(adminPassword),
    operation: 'list_ota_data_process_admin',
  };
  if (opts?.month?.trim()) {
    q.month = opts.month.trim();
  }
  if (opts?.year?.trim()) {
    q.year = opts.year.trim();
  }
  if (opts?.period?.trim()) {
    q.period = opts.period.trim();
  }
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: q,
    },
    false
  );

  if (typeof res === 'string') {
    return { ok: false, error: res };
  }

  const msg = unwrapAdminMessage(res);
  if (!msg) {
    return { ok: false, error: 'Unexpected response from admin API.' };
  }
  if (msg.error === 'Not a Admin User') {
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (msg.error) {
    return { ok: false, error: String(msg.error) };
  }
  if (Array.isArray(msg.rows)) {
    return {
      ok: true,
      data: {
        rows: msg.rows as OtaDataProcessRow[],
      },
    };
  }
  return { ok: false, error: 'Unexpected OTA data process response shape.' };
};

export type BookingSupplyLogPayload = {
  filename: string;
  content_base64: string;
};

function downloadBase64AsFile(contentBase64: string, filename: string) {
  const binary = atob(contentBase64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** DB/API source label for OTA supply rows; maps to EC2 .../logs/{booking|agoda|expedia}/ */
export type OtaSupplyLogSource = 'booking.com' | 'agoda' | 'hotels.com';

/** getAirbticsUserDetails: operation=get_ota_supply_batch_log_admin */
export const fetchOtaSupplyBatchLogAdmin = async (
  adminPassword: string,
  crawlDate: string,
  source: OtaSupplyLogSource
): Promise<
  { ok: true; data: BookingSupplyLogPayload } | { ok: false; error: string }
> => {
  const q: Record<string, string> = {
    admin_password: encodeURIComponent(adminPassword),
    operation: 'get_ota_supply_batch_log_admin',
    crawl_date: crawlDate.trim(),
    source: source.trim(),
  };
  const res: any = await callApi(
    GET_AIRBTICS_USER_DETAILS_URL,
    {
      queryParams: q,
    },
    false
  );

  if (typeof res === 'string') {
    return { ok: false, error: res };
  }

  const msg = unwrapAdminMessage(res);
  if (!msg) {
    return { ok: false, error: 'Unexpected response from admin API.' };
  }
  if (msg.error === 'Not a Admin User') {
    return { ok: false, error: 'Admin password incorrect.' };
  }
  if (msg.error) {
    return { ok: false, error: String(msg.error) };
  }
  const fn = msg.filename;
  const b64 = msg.content_base64;
  if (typeof fn === 'string' && typeof b64 === 'string' && fn.length > 0 && b64.length > 0) {
    return {
      ok: true,
      data: { filename: fn, content_base64: b64 },
    };
  }
  return { ok: false, error: 'Unexpected OTA supply log response shape.' };
};

/** @deprecated Prefer fetchOtaSupplyBatchLogAdmin(..., 'booking.com') */
export const fetchBookingSupplyLogAdmin = async (
  adminPassword: string,
  crawlDate: string
): Promise<
  { ok: true; data: BookingSupplyLogPayload } | { ok: false; error: string }
> => fetchOtaSupplyBatchLogAdmin(adminPassword, crawlDate, 'booking.com');

export function triggerBookingSupplyLogDownload(payload: BookingSupplyLogPayload, downloadFilename?: string) {
  downloadBase64AsFile(payload.content_base64, downloadFilename || payload.filename);
}

export const getUserSubscriptionDetails = async (email: string, adminPassword: string, setUserSubscriptionDetails, setUserCurrentSubscriptionDetailsError, extraParamsToFetchHistory = {}) => {
  const URL =
    'https://w2g9kh7lf4.execute-api.us-east-2.amazonaws.com/default/fetchCustomerSubscriptionDetails';

  const userDetails = await callApi(
    URL,
    {
      queryParams: {
        // isTest: true,
        adminRequestedEmail: email,
        adminPassword: adminPassword,
        ...extraParamsToFetchHistory,
      },
    },
    true,
    false,
    '',
    'GET',
    true
  );

  console.log(userDetails);

  if (userDetails.subscriptions) {
    userDetails &&
      !userDetails.error &&
      userDetails.subscriptions &&
      userDetails.subscriptions.length > 0 &&
      setUserSubscriptionDetails(userDetails.subscriptions);
  } else {
    userDetails && !userDetails.error && setUserSubscriptionDetails(userDetails);
  }

  userDetails && userDetails.error && setUserCurrentSubscriptionDetailsError("You don't have any subscriptions.");
};


export const getUserSubscriptionHistory = async (email: string, adminPassword: string, setUserSubscriptionHistory, setUserSubscriptionHistoryError, refresh = false) => {
  const extraParamsToFetchHistory = {
    fetchSubscriptionHistory: true,
    refreshData: refresh,
  };
  getUserSubscriptionDetails(email, adminPassword, setUserSubscriptionHistory, setUserSubscriptionHistoryError, extraParamsToFetchHistory);
}


export const getPlanLabelFromPriceID = (priceID: string) => {
  const planEntries = Object.entries(ProductIDOptions);

  for (const [planKey, { productID }] of planEntries) {
    const matchedInterval = Object.entries(productID).find(
      ([, id]) => id === priceID
    );

    if (matchedInterval) {
      const [interval] = matchedInterval;
      const capitalizedPlan = capitalize(planKey);
      const capitalizedInterval = capitalize(interval);
      return ({
        text: `${capitalizedPlan} – ${capitalizedInterval}`,
        planKey: planKey,
        interval: interval,
      });
    }
  }

  return {
    text: priceID,
    planKey: null,
    interval: null,
  };
};

export const getPlanDurationFrequency = (activeSubscriptionDetails) => {
  if(!activeSubscriptionDetails.length) return null;
  activeSubscriptionDetails = activeSubscriptionDetails[activeSubscriptionDetails.length - 1];
  const startDatesArray = activeSubscriptionDetails.start_date;
  const endDatesArray = activeSubscriptionDetails.end_date;

  // Format: YYYY-MM-DD string
  const latestStartDate = startDatesArray[startDatesArray.length - 1];
  const latestEndDate = endDatesArray[endDatesArray.length - 1];

  const duration = new Date(latestEndDate).getTime() - new Date(latestStartDate).getTime();
  const durationInDays = Math.ceil(duration / (1000 * 60 * 60 * 24));

  console.log("DAY DAY DAY:", durationInDays);

  const frequency = durationInDays < 365 ? 'month' : 'year';

  return frequency;
}


export const clearRateLimits = async (email: string, adminPassword: string) => {
  const URL = 'https://v2moz5pks1.execute-api.us-east-2.amazonaws.com/prod/rateLimitActionsByAdmin';
  const response = await callApi(URL, {
    queryParams: {
      email: encodeURIComponent(email),
      adminPassword: encodeURIComponent(adminPassword),
      action: "clear_all_rate_limit_actions"
    },
  }, true, false, '', 'GET', true);

  return response;
}
